/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import _, { isEqual, sortBy } from 'lodash'

import type { EuiCallOut } from '@elastic/eui'
import {
  EuiButton,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiSpacer,
  EuiText,
  EuiTitle,
  htmlIdGenerator,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import AddTagsForm from './AddTagsForm'
import TagsTable from './TagsTable'
import SavePendingChangesModal from './SavePendingChangesModal'

import type { ValidationErrors } from './AddTagsForm'
import type { ReactElement } from 'react'
import type { Tag } from '../types'

export type TagsFlyoutProps = {
  initialTags: Tag[]
  onClose: () => void
  onSaveTagsMutation: (tags: Tag[]) => Promise<void>
  isLoading: boolean
  resourceType?: ReactElement<typeof FormattedMessage>
  resourceTypePlural?: ReactElement<typeof FormattedMessage>
  validateTag: (currentTag: Tag, currentTags: Tag[]) => ValidationErrors
  errorCallout?: ReactElement<typeof EuiCallOut> | null
}

const tagsHaveChanged = (initialTags: Tag[], currentTags: Tag[]): boolean =>
  !isEqual(sortBy(initialTags, ['key', 'value']), sortBy(currentTags, ['key', 'value']))

const TagsFlyout = ({
  initialTags,
  onClose,
  onSaveTagsMutation,
  isLoading,
  resourceType = <FormattedMessage id='resource-tags.resource-type' defaultMessage='resource' />,
  resourceTypePlural = (
    <FormattedMessage id='resource-tags.resource-type-plural' defaultMessage='resources' />
  ),
  validateTag,
  errorCallout,
}: TagsFlyoutProps) => {
  const [tags, setTags] = useState(initialTags)
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false)

  const modalTitleId = htmlIdGenerator()('modalTitle')

  const onRemoveTag = (tag: Tag) => {
    setTags((prevState) => prevState.filter((t) => !_.isEqual(t, tag)))
  }

  const onUpdateTag = (oldTag: Tag, newTag: Tag) => {
    setTags((prevState) => prevState.map((t) => (_.isEqual(t, oldTag) ? newTag : t)))
  }

  const tagsChanged = tagsHaveChanged(initialTags, tags)

  const onSaveTagsHandler = async () => {
    if (tagsChanged) {
      await onSaveTagsMutation(tags)
    } else {
      onClose()
    }

    setShowCancelConfirmModal(false)
  }

  const onCancel = () => {
    if (tagsChanged) {
      setShowCancelConfirmModal(true)
    } else {
      onClose()
    }
  }

  const [isAddingNewTag, setIsAddingNewTag] = useState(false)

  return (
    <EuiFlyout
      aria-labelledby={modalTitleId}
      maxWidth='32rem'
      size='l'
      ownFocus={true}
      onClose={onCancel}
    >
      <EuiFlyoutHeader hasBorder={true}>
        <EuiTitle size='m'>
          <h2 id={modalTitleId}>
            <FormattedMessage id='resource-tags.flyout.title' defaultMessage='Tags' />
          </h2>
        </EuiTitle>

        <EuiSpacer size='s' />

        <EuiText color='subdued' size='s'>
          <FormattedMessage
            id='resource-tags.flyout.subtitle'
            defaultMessage='Tags make {resources} easier to find. Use them to provide additional metadata about a {resource} and its usage.'
            values={{
              resources: resourceTypePlural,
              resource: resourceType,
            }}
          />
        </EuiText>
      </EuiFlyoutHeader>

      <EuiFlyoutBody>
        <AddTagsForm
          currentTags={tags}
          setCurrentTags={setTags}
          validateTag={validateTag}
          onFocus={() => setIsAddingNewTag(true)}
        />

        <EuiSpacer size='l' />

        {tags.length > 0 && (
          <TagsTable
            tags={tags}
            onRemoveTag={onRemoveTag}
            onUpdateTag={onUpdateTag}
            validateTag={validateTag}
            isAddingNewTag={isAddingNewTag}
            setIsAddingNewTag={setIsAddingNewTag}
          />
        )}

        {showCancelConfirmModal && (
          <SavePendingChangesModal
            onClose={onClose}
            onSave={onSaveTagsHandler}
            isLoading={isLoading}
          />
        )}

        {errorCallout && (
          <Fragment>
            <EuiSpacer size='l' />
            <EuiFlexItem grow={false}>{errorCallout}</EuiFlexItem>
          </Fragment>
        )}
      </EuiFlyoutBody>

      <EuiFlyoutFooter>
        <EuiFlexGroup justifyContent='spaceBetween'>
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty onClick={onCancel} flush='left'>
              <FormattedMessage id='resource-tags.flyout.cancel' defaultMessage='Cancel' />
            </EuiButtonEmpty>
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            <EuiButton fill={true} isLoading={isLoading} onClick={onSaveTagsHandler}>
              <FormattedMessage id='resource-tags.flyout.send-tags' defaultMessage='Save' />
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </EuiFlyout>
  )
}

export default TagsFlyout
